<template lang="html">
    <div class="p-1">
        <div class="flex" @mouseover="showCross = true" @mouseout="showCross = false" v-show="peer.role != 'controls'">
            <sound-meter ref="soundMeter" :id="(peer.public_key || 'unknown').toString()" :stream="stream" class="mr-1 h-8 w-1" v-show="showSoundmeter" v-on:speaking="onSpeaking()"></sound-meter>
            <sound-meter ref="secondarySoundMeter" :id="'sec_' + (peer.public_key || 'unknown').toString()" :stream="secondaryStream" class="mr-2 h-8 w-1" v-show="secondaryStream"></sound-meter>
            <div class="flex justify-between flex-grow" :class="peerStatusMap[peer.status]">
                <div class="text-sm font-medium text-gray-600 dark:text-gray-200 leading-none">
                    <div class="flex">
                        <div class="flex-grow">
                        {{peer.name}}
                        </div>
                    </div>
                    <div class="flex space-x-1 items-center mt-1">
                        <div>
                            <span class="uppercase text-xs font-semibold flex items-center status"
                                  :class="statusMap[peer.status]"
                                  :title="peer.status">
                                <!-- Circle icon -->
                                <svg class="h-3 w-3 inline-block fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 3a7 7 0 1 0 .001 13.999A7 7 0 0 0 10 3z"/></svg>
                            </span>
                        </div>
                      <div class="font-medium text-gray-400 role capitalize">{{ peer.role }}</div>
                    </div>
                </div>
                <div class="flex items-center" v-show="showActions">
                    <button class="underline text-gray-400 hover:text-red-600" @click='proxyHandleRemoteHangup(peer.public_key)' v-show="showCross" data-tippy-content="Disconnect user">
                        <!-- Close/times icon -->
                        <svg class="h-6 w-6 fill-current" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z" clip-rule="evenodd"/></svg>
                    </button>
                    <button class="underline text-green-500 hover:text-green-300" @click='connectPeer()' :class="{'hidden': peer.status != 'waiting', 'btn-disabled': callingPeer}" :disabled="callingPeer">
                        Connect
                    </button>
                    <div class="text-yellow-500" :class="{'hidden': peer.status != 'connecting'}">
                        Connecting
                    </div>
                    <button type="button" class="text-gray-400 hover:text-gray-500" @click="showStats = !showStats" data-tippy-content="Show more information" v-show="peer.status =='connected'">
                        <!-- Chevron icon -->
                        <svg class="w-5 h-5 fill-current transform transition" :class="{'rotate-90': !showStats}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M4.516 7.548c.436-.446 1.043-.481 1.576 0L10 11.295l3.908-3.747c.533-.481 1.141-.446 1.574 0 .436.445.408 1.197 0 1.615-.406.418-4.695 4.502-4.695 4.502a1.095 1.095 0 0 1-1.576 0S4.924 9.581 4.516 9.163c-.409-.418-.436-1.17 0-1.615z"/></svg>
                    </button>
                </div>
            </div>
        </div>
        <div class="bg-white dark:bg-gray-700 p-2" v-show="showStats && showActions && peer.status == 'connected'">
            <div class="flex flex-wrap justify-between items-center hidden">
                <div class="w-full">
                    <audio :id="'aux_audio_'+ peer.public_key"          class="w-full" controls autoplay playsinline  :data-role="peer.role"></audio>
                    <audio :id="'audio_'+ peer.public_key"              class="w-full" controls autoplay playsinline :data-role="peer.role"></audio>
                    <audio :id="'director_to_set_'+ peer.public_key"    class="w-full" controls autoplay playsinline :data-role="peer.role"></audio>
                    <audio :id="'camera_audio_'+ peer.public_key"       class="w-full" controls autoplay playsinline :data-role="peer.role"></audio>
                </div>
            </div>
            <div class="flex justify-between">
            	<div class="flex flex-wrap space-x-2 items-center">
                    <div>
                        AEC ON
                    </div>
                    <div class="form-switch toggleAECCommand" :data-id='peer.public_key' data-tippy-content="Turn this on if the user has headphones to improve the quality of their audio.">
                        <input type="checkbox" checked>
                        <i></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>

import { detectBrowser } from 'utils/browser';
import { localNotify } from 'utils/notifications'
import messageBroker from 'general/messageBroker';
import hooksManager from 'general/HooksManager';

import { getPeerStatusMap, getStatusMap, connectPeer, kickPeer } from "common/participant"
import tippy from 'tippy.js';

export default {
  props: ['peer', 'showActions'],
  data() {
    return {
      stream: null,
      secondaryStream: null,
      showCross: false,
      showStats: false,

      showSoundmeter: true,
      callingPeer: false,
      statusMap: {},
      peerStatusMap: {},
    }
  },
  mounted() {
    this.role = role; // Global role, setting it tolocal

    if(this.role == "guest" && this.peer.role == "talent" || 
       this.role == "talent" && this.peer.role == "guest"){
      this.showSoundmeter = false;
    }

    this.statusMap = getStatusMap();
    this.peerStatusMap = getPeerStatusMap();
  },
  methods: {
    addStream(stream){
      this.stream = stream;
    },
    addSecondaryStream(stream){
      this.secondaryStream = stream;
    },
    handleRemoteHangup(target) {
      localNotify("Peer disconnected", 'error')
      if(role == 'host'){
        // tell the server to disconnect the peer
        messageBroker.send({
          to: "server",
          type: "disconnect-peer",
          body: target + "" // to ensure it is a string
        })
      }
      this.$emit('removePeer')
    },
    proxyHandleRemoteHangup(peer_key){
      kickPeer(window.room_key, peer_key)
    },
    updateStatus(){
      this.peer
    },
    updatePeerInfo(data){
      const message = data.message;
      this.peer.audio_input = message.selectedInput;
      this.peer.download_speed = message.downloadSpeed;
      this.peer.upload_speed = message.uploadSpeed;
      this.isRecording = message.isRecording
    },
    connectPeer(){
      this.peer.status = 'connecting'
      this.callingPeer = true;
      // disable settings
      $(".audioSource[data-id=0]").attr('disabled', 'disabled').addClass("btn-disabled");
      // disable connected buttons
      window.setTimeout(() => {
        $(".audioSource[data-id=0]").removeAttr('disabled', 'disabled').removeClass("btn-disabled");

        this.callingPeer = false;
      }, 100)

      let public_key = this.peer.public_key;
      connectPeer(window.room_key, this.peer.public_key)
      // create the PC on the RTC manager with this peer's data
    },
    onSpeaking(){
      this.$emit('speaking', this.peer.public_key)
    }
  },

}
</script>
<style lang="sass">
  .status{
  transition: all 0.2s ease-in-out;
  }

</style>